import { ImportResult, CsvImportResultDef, ImportErrorInfo, ImportContentErrorInfo } from "@/util/import/ImportResultModel";
import { HeaderSpecItem } from "@/util/import/CsvTypes";
import { formatedDateTime, createImportErrorInfo, createHeaderErrorMessage } from "@/util/import/ImportUIHelper";
import { parseCsvAs } from "@/util/import/CsvParser";
import { MessageUtil } from '@/models/Message/MessageUtil';
import _ from 'lodash';
import { AndroidAgentSetting } from '@/views/Mobile/Android/GoogleAccountSetting/model/AndroidAgentSetting';
import { AMAPI_Policy, AMAPI_WorkAccountSetupConfig } from '@/models/Mobile/Android/AndroidManagementV1Schema';
import * as schemaConstants from '@/models/Mobile/Android/AndroidManagementV1SchemaConstants';

// エラーレポート定義
const ImportErrorReason = {
    // 端末機No.
    AGENT_NO_NOT_NUMBER: "半角数字で指定してください。",
    AGENT_NO_DUPLICATED: "[%s]行目に同一の端末機No.が指定されています。",
    UNKNOWN_AGENTID: "閲覧権限のある\"モバイル端末機(Android)\"を指定してください。",
    // Googleアカウントでのログイン
    AUTH_TYPE_INVALID: "\"必須\"か\"任意\"で指定してください。",
    // Googleアカウント
    EMAIL_INVALID_FORMAT: "メールアドレスの形式で指定してください。",
    EMAIL_TOO_LONG: "半角256文字以内で指定してください。"
} as const;

// 認証タイプ
export const IMPORT_AUTH_TYPE = {
    NOT_ENFORCED: "任意",
    GOOGLE_AUTHENTICATED: "必須"
} as const;

// ヘッダIndex定義
const headerIndex = {
    HEADER_INDEX_AGENT_NO: 0,
    HEADER_INDEX_AUTH_TYPE: 1,
    HEADER_INDEX_EMAIL: 2,
}

// ヘッダ定義
const headerSpec: HeaderSpecItem[] = [
    { name: "端末機No.", required: true, trim: true, mapTo: "agentNo" },
    { name: "Googleアカウントでのログイン", required: true, trim: true, mapTo: "authenticationType" },
    { name: "Googleアカウント", required: true, trim: true, mapTo: "requiredAccountEmail" },
];

export interface ExecuteImportResult {
    result: boolean;
    data: AndroidAgentSetting[];
    errorReport: string;
}

export interface GoogleAccountSettingImportInfo {
    agentNo: string;
    authenticationType: string;
    requiredAccountEmail: string;
}

interface ImportInfoWithLine {
    importData: GoogleAccountSettingImportInfo;
    line: number;
}

// ----------------------------------------------------------------------------------
// インポートメイン処理
// ----------------------------------------------------------------------------------
export async function ExecuteImport(file: File, orgDataList: AndroidAgentSetting[], allowableAgentNos: Set<number>): Promise<ExecuteImportResult> {
    const checkStartDate = formatedDateTime();
    let retData: ExecuteImportResult = {
        result: false,
        data: _.cloneDeep(orgDataList),
        errorReport: ""
    };
    const importResult = new ImportResult();
    importResult.startTimeUTC = checkStartDate;
    importResult.result = CsvImportResultDef.Success;

    // 1. CSVパース処理
    const result = await parseCsvAs<GoogleAccountSettingImportInfo>(file, {
        existHeader: true,
        headerSpec,
        mapRow: (row) => ({
            agentNo: row.agentNo ?? "",
            authenticationType: row.authenticationType ?? "",
            requiredAccountEmail: row.requiredAccountEmail ?? ""
        }),
    });

    if (!result.ok) {
        const errorMessage = createHeaderErrorMessage(headerSpec, result.errors, importResult);
        MessageUtil.MessageBoxV2(errorMessage.strHTML, null, null, errorMessage.bit);
        return retData;
    }

    if (!result.rows || result.rows.length <= 0) {
        importResult.result = CsvImportResultDef.NoData;
        const errorMessage = createImportErrorInfo(importResult, "");
        MessageUtil.MessageBoxV2(errorMessage.strHTML, null, null, errorMessage.bit);
        return retData;
    }

    // 2. CSVデータの各行チェック（関数化）
    const validImportDataList = validateImportRows(result.rows, allowableAgentNos, importResult);

    // 3. エラーがある場合はレポートを作成して終了
    if (importResult.result !== CsvImportResultDef.Success) {
        importResult.endTimeUTC = formatedDateTime();
        const message = createImportErrorInfo(importResult, "");
        retData.errorReport = message.strHTML;
        return retData;
    }

    // 4. データの上書き・追加処理（関数化）
    applyValidImportData(validImportDataList, retData.data);

    // 5. AgentNoで昇順ソート
    retData.data.sort((a, b) => a.agentNo - b.agentNo);

    // 成功
    retData.result = true;
    return retData;
}

// ----------------------------------------------------------------------------------
// 切り出した処理関数
// ----------------------------------------------------------------------------------

/**
 * CSVの各行をバリデーションし、正常なデータのリストを返します。
 * エラーがある場合は importResult にエラー情報を蓄積します。
 */
function validateImportRows(
    rows: Array<{ row: GoogleAccountSettingImportInfo; lineNumber: number }>,
    allowableAgentNos: Set<number>,
    importResult: ImportResult
): ImportInfoWithLine[] {
    const validImportDataList: ImportInfoWithLine[] = [];
    
    // 何行目に存在したかを保持するため Map に変更
    const processedAgentNos = new Map<string, number>();

    rows.forEach(row => {
        let isError = false;
        const info: ImportInfoWithLine = {
            importData: {
                agentNo: row.row.agentNo,
                authenticationType: row.row.authenticationType,
                requiredAccountEmail: row.row.requiredAccountEmail
            },
            line: row.lineNumber
        };

        const importErrorInfo = new ImportErrorInfo();
        importErrorInfo.line = row.lineNumber;

        // バリデーションチェック（端末機No）
        {
            const contentErrorInfo = new ImportContentErrorInfo();
            contentErrorInfo.property = headerSpec[headerIndex.HEADER_INDEX_AGENT_NO].name;
            if (!IsValidAgentNo(info.importData, contentErrorInfo, processedAgentNos, allowableAgentNos)) {
                isError = true;
                importResult.result = CsvImportResultDef.ContentIssue;
                importErrorInfo.info.push(contentErrorInfo);
            }
        }

        // バリデーションチェック（ログイン設定）
        {
            const contentErrorInfo = new ImportContentErrorInfo();
            contentErrorInfo.property = headerSpec[headerIndex.HEADER_INDEX_AUTH_TYPE].name;
            if (!IsValidAuthenticationType(info.importData, contentErrorInfo)) {
                isError = true;
                importResult.result = CsvImportResultDef.ContentIssue;
                importErrorInfo.info.push(contentErrorInfo);
            }
        }

        // バリデーションチェック（アカウントメール）
        {
            const contentErrorInfo = new ImportContentErrorInfo();
            contentErrorInfo.property = headerSpec[headerIndex.HEADER_INDEX_EMAIL].name;
            if (!IsValidRequiredAccountEmail(info.importData, contentErrorInfo)) {
                isError = true;
                importResult.result = CsvImportResultDef.ContentIssue;
                importErrorInfo.info.push(contentErrorInfo);
            }
        }

        if (isError) {
            (importResult.errors ??= []).push(importErrorInfo);
        } else {
            validImportDataList.push(info);
            // 正常と判定された場合のみ重複チェック用Mapに登録
            processedAgentNos.set(info.importData.agentNo, row.lineNumber);
        }
    });

    return validImportDataList;
}

/**
 * 正常と判定されたデータを、更新用配列（retData.data）へ上書きまたは追加します。
 */
function applyValidImportData(validImportDataList: ImportInfoWithLine[], currentData: AndroidAgentSetting[]): void {
    const existingIndexMap = new Map<number, number>(
        currentData.map((data, index) => [data.agentNo, index])
    );

    validImportDataList.forEach(validData => {
        const parsedAgentNo = parseInt(validData.importData.agentNo, 10);
        const existingIndex = existingIndexMap.get(parsedAgentNo) ?? -1;

        // CSVの「任意」「必須」表記からAMAPIの認証タイプEnum値へ変換
        let convertedAuthType: string = schemaConstants.AMAPI_AuthenticationType_Enums.authentication_type_not_enforces;
        if (validData.importData.authenticationType === IMPORT_AUTH_TYPE.GOOGLE_AUTHENTICATED) {
            convertedAuthType = schemaConstants.AMAPI_AuthenticationType_Enums.googleAuthEnticated;
        }

        const newSetupConfig: AMAPI_WorkAccountSetupConfig = {
            authenticationType: convertedAuthType,
            requiredAccountEmail: validData.importData.requiredAccountEmail
        };

        if (existingIndex >= 0) {
            // 上書き処理
            if (!currentData[existingIndex].setting) {
                currentData[existingIndex].setting = new AMAPI_Policy();
            }
            currentData[existingIndex].setting.workAccountSetupConfig = newSetupConfig;
        } else {
            // 追加処理
            const newPolicy = new AMAPI_Policy();
            newPolicy.workAccountSetupConfig = newSetupConfig;
            currentData.push({
                agentNo: parsedAgentNo,
                setting: newPolicy
            });
        }
    });
}

// ----------------------------------------------------------------------------------
// バリデーション関数群
// ----------------------------------------------------------------------------------

function IsValidAgentNo(
    info: GoogleAccountSettingImportInfo, 
    errorInfo: ImportContentErrorInfo,
    processedAgentNos: Map<string, number>,
    allowableAgentNos: Set<number>
): boolean {
    const agentNoStr = info.agentNo;

    // 1. 数値ではない
    const parsed = Number(agentNoStr);
    if (agentNoStr === "" || Number.isNaN(parsed) || !Number.isInteger(parsed)) {
        errorInfo.errorDetail.push(ImportErrorReason.AGENT_NO_NOT_NUMBER);
        return false;
    }

    // 2. ファイル内重複チェック(N行目にすでにあるよ)
    if (processedAgentNos.has(agentNoStr)) {
        const duplicatedLine = processedAgentNos.get(agentNoStr);
        errorInfo.errorDetail.push(ImportErrorReason.AGENT_NO_DUPLICATED.replace("%s", String(duplicatedLine)));
        return false;
    }

    // 3. allowableAgentNos に存在しない端末が指定された
    const parsedAgentNo = parseInt(agentNoStr, 10);
    if (!allowableAgentNos.has(parsedAgentNo)) {
        errorInfo.errorDetail.push(ImportErrorReason.UNKNOWN_AGENTID);
        return false;
    }

    return true;
}

function IsValidAuthenticationType(info: GoogleAccountSettingImportInfo, errorInfo: ImportContentErrorInfo): boolean {
    const authType = info.authenticationType;
    
    // IMPORT_AUTH_TYPEに含まれない
    if (authType !== IMPORT_AUTH_TYPE.NOT_ENFORCED && authType !== IMPORT_AUTH_TYPE.GOOGLE_AUTHENTICATED) {
        errorInfo.errorDetail.push(ImportErrorReason.AUTH_TYPE_INVALID);
        return false;
    }
    
    return true;
}

function IsValidRequiredAccountEmail(info: GoogleAccountSettingImportInfo, errorInfo: ImportContentErrorInfo): boolean {
    const email = info.requiredAccountEmail;

    // 空文字はOK
    if (!email) {
        return true;
    }

    // メールアドレスのformatでない (簡易的な正規表現によるチェック)
    if (!email.isValidMailAddress2()) {
        errorInfo.errorDetail.push(ImportErrorReason.EMAIL_INVALID_FORMAT);
        return false;
    }

    // 256over
    if (email.length > 256) {
        errorInfo.errorDetail.push(ImportErrorReason.EMAIL_TOO_LONG);
        return false;
    }

    return true;
}